import type { Design } from '@/lib/services/designs/designs.types'
import type { AdminRouterContext } from '@/lib/router-context'
import { RecordHistoryPage } from '@/components/audit-log/RecordHistoryPage'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { auditLogQueries } from '@/lib/services/audit-log/audit-log.query'
import { designQueries } from '@/lib/services/designs/designs.query'
import { designSchema } from '@/lib/services/designs/designs.schema'
import { createFileRoute, notFound } from '@tanstack/react-router'

export const Route = createFileRoute('/_authenticated/$orgId/designs/$designId_/history')({
  component: DesignHistoryPage,
  params: {
    parse: (params) => designSchema.pick({ designId: true }).parse(params),
  },
  head: adminRouteTitleHead,
  loader: loadDesignHistory,
})

export async function loadDesignHistory({
  context,
  params,
}: {
  context: AdminRouterContext
  params: { orgId: number | string; designId: number | string }
}): Promise<{ design: Design }> {
  if (!context.ability.can('view', 'audit-logs') || !context.ability.can('view', 'designs')) {
    throw notFound()
  }

  const [design] = await Promise.all([
    context.queryClient.ensureQueryData(
      designQueries.show({
        organizationId: params.orgId,
        designId: params.designId,
      })
    ),
    context.queryClient.ensureInfiniteQueryData(
      auditLogQueries.record({
        organizationId: params.orgId,
        subject: 'designs',
        recordId: params.designId,
      })
    ),
  ])

  return { design }
}

export function DesignHistoryPage() {
  const { orgId, designId } = Route.useParams()
  const { design } = Route.useLoaderData()
  const path = usePathHelper()
  const backLink = design.is_default
    ? path.builder.index().using({ orgId }).search({ section: 'brand-layout' }).link
    : path.designs.show(designId).link

  return (
    <RecordHistoryPage
      backLabel={design.is_default ? 'website builder' : 'seasonal website update'}
      backLink={backLink}
      description="Review important changes to this website design, its content, activation, and schedule."
      organizationId={design.organization_id ?? orgId}
      recordId={designId}
      recordLabel={design.name || 'Website design'}
      subject="designs"
    />
  )
}
